/**
 * Utilities for calculating subscription prices and discounts
 */

// Products that get bonus discounts for subscription periods
const BONUS_ELIGIBLE_PRODUCTS = [
  'business-phone',
  'fmc',
  'call-tracking',
  'callback',
  'omnichannel-chat',
  'ai-conversation'  // Added: AI gets subscription bonus like other addons
];

// Products that don't get bonus discounts
const NO_BONUS_PRODUCTS = [
  'eniq-ai', // per-user add-on price is not discounted for annual payment
  'voicebot',
  'phone-numbers',
  'voice-traffic'
];

/**
 * Calculate subscription pricing for different periods
 * @param {Array} products - Array of products with prices (already with annual discount applied)
 * @param {Object} options - Subscription options
 * @param {boolean} options.show360Days - Show 360 days option
 * @param {boolean} options.show180Days - Show 180 days option  
 * @param {boolean} options.show90Days - Show 90 days option
 * @param {boolean} options.show12Month - Show 12-month contract
 * @param {boolean} options.showSplitPayment - Show split payment (Ukraine only)
 * @param {string} region - Current region
 * @param {number} bundleDiscountedTotal - Optional bundle-discounted total to use as baseline
 * @param {Array} bundleDiscountedProducts - Optional bundle-discounted products
 * @param {number} customDiscount360 - Custom discount percentage for 360 days (default: 20%)
 * @param {number} customDiscount180 - Custom discount percentage for 180 days (default: 0%)
 * @returns {Object} Calculated subscription plans
 */
export const calculateSubscriptionPlans = (products, options = {}, region = 'eu', bundleDiscountedTotal = null, bundleDiscountedProducts = null, customDiscount360 = 20, customDiscount180 = 0) => {
  // Map product labels to IDs for bonus eligibility checking
  const getProductIdFromLabel = (label, productType) => {
    // First check if productType is available (more reliable)
    if (productType) return productType;
    
    // Fallback to label-based detection
    if (label.includes('Business Phone')) return 'business-phone';
    if (label.includes('FMC')) return 'fmc';
    if (label.includes('Call Tracking')) return 'call-tracking';
    if (label.includes('Callback')) return 'callback';
    if (label.includes('Omnichannel Chat')) return 'omnichannel-chat';
    if (label.includes('Chat')) return 'chat';
    if (label.includes('AI Conversation') || label.includes('Мовна аналітика з ШІ')) return 'ai-conversation';
    if (label.includes('Eniq AI')) return 'eniq-ai';
    if (label.includes('Voice bot') || label.includes('Голосовий бот') || label.includes('Голосовой бот') || label.includes('Bot głosowy')) return 'voicebot';
    if (label.includes('Voice Traffic') || label.includes('Пакети хвилин')) return 'voice-traffic';
    if ((label.includes('numbers') || label.includes('номерів')) && !label.includes('Call Tracking')) return 'phone-numbers';
    return 'unknown';
  };
  
  // Use bundle-discounted products if available, otherwise fall back to base prices
  const productsToUse = bundleDiscountedProducts || products;
  
  // Separate bonus-eligible and non-bonus products
  const bonusEligibleProducts = productsToUse.filter(product => {
    const productId = getProductIdFromLabel(product.label || product.title || '', product.productType);
    return BONUS_ELIGIBLE_PRODUCTS.includes(productId);
  });
  
  const noBonusProducts = productsToUse.filter(product => {
    const productId = getProductIdFromLabel(product.label || product.title || '', product.productType);
    return NO_BONUS_PRODUCTS.includes(productId);
  });
  
  // Calculate totals for different product groups
  // These are now BUNDLE-DISCOUNTED prices (if bundleDiscountedProducts provided)
  const bonusEligibleBundleTotal = bonusEligibleProducts
    .reduce((total, product) => total + (product.price || 0), 0);
    
  const noBonusBundleTotal = noBonusProducts
    .reduce((total, product) => total + (product.price || 0), 0);
  
  // Calculate base monthly cost (with bundle discounts but without subscription discounts)
  const baseMonthlyCostNoDiscounts = bonusEligibleBundleTotal + noBonusBundleTotal;
  
  // For backwards compatibility, also calculate base prices totals
  const bonusEligibleBaseTotal = products.filter(product => {
    const productId = getProductIdFromLabel(product.label || product.title || '', product.productType);
    return BONUS_ELIGIBLE_PRODUCTS.includes(productId);
  }).reduce((total, product) => total + (product.price || 0), 0);
    
  const noBonusBaseTotal = products.filter(product => {
    const productId = getProductIdFromLabel(product.label || product.title || '', product.productType);
    return NO_BONUS_PRODUCTS.includes(productId);
  }).reduce((total, product) => total + (product.price || 0), 0);

  const plans = [];
  
  // Default 360 days plan: Apply custom subscription discount only to bonus-eligible products
  let plan360Cost, plan360Annual;
  // Apply custom subscription discount to bonus-eligible products (keeping bundle discounts)
  const discountFactor360 = 1 + (customDiscount360 / 100); // Convert percentage to factor
  const plan360BonusTotal = bonusEligibleBundleTotal / discountFactor360; // Custom subscription discount to bundle-discounted eligible products
  plan360Cost = plan360BonusTotal + noBonusBundleTotal; // Add no-bonus products without subscription discount (but with bundle discounts)
  plan360Annual = plan360Cost * 12;
  
  // Check if we should show savings/overpayment (when additional subscription types are enabled)
  const showSavings = options.show180Days || options.show90Days || options.show12Month || options.showSplitPayment;

  plans.push({
    id: '360-days',
    name: showSavings ? `360 days (${customDiscount360}% bonus)` : '360 days',
    monthlyPrice: plan360Cost,
    annualPrice: plan360Annual,
    paymentAmount: plan360Annual, // Full year payment
    isDefault: true,
    isRecommended: true,
    savings: 0, // Will be calculated if other options are shown
    color: 'success'
  });

  if (showSavings) {
    // Calculate savings for 360 days plan (reference point)
    const referenceMonthly = baseMonthlyCostNoDiscounts; // Use bundle-discounted prices without subscription discounts as reference
    const annualSavings360 = referenceMonthly * 12 - plan360Annual;
    plans[0].savings = annualSavings360;
    plans[0].showSavings = true;
  }

  // 180 days plan (legacy option): Apply custom smaller subscription discount only to bonus-eligible
  if (options.show180Days) {
    let plan180Cost, plan180Annual;
    // Apply custom smaller subscription discount to bonus-eligible products (keeping bundle discounts)
    const discountFactor180 = 1 + (customDiscount180 / 100); // Convert percentage to factor
    const plan180BonusTotal = bonusEligibleBundleTotal / discountFactor180; // Custom subscription discount to bundle-discounted eligible products
    plan180Cost = plan180BonusTotal + noBonusBundleTotal; // Add no-bonus products without subscription discount (but with bundle discounts)
    plan180Annual = plan180Cost * 12;
    const plan180SemiAnnual = plan180Cost * 6; // 180 days payment
    
    // Calculate savings for 180 days
    const referenceMonthly = baseMonthlyCostNoDiscounts; // Use bundle-discounted prices as reference
    const savings180Annual = referenceMonthly * 12 - plan180Annual; // annual savings (keep for overpayment logic)
    const displaySavings180 = referenceMonthly * 6 - plan180SemiAnnual; // show half-year savings in UI
    // Overpayment relative to 360 days savings (annual comparison remains unchanged)
    const overpayment = (referenceMonthly * 12 - plan360Annual) - savings180Annual;
    
    plans.push({
      id: '180-days',
      name: `180 days (${customDiscount180}% bonus)`,
      monthlyPrice: plan180Cost,
      annualPrice: plan180Annual,
      semiannualPrice: plan180SemiAnnual,
      paymentAmount: plan180SemiAnnual, // 180 days payment
      isDefault: false,
      savings: displaySavings180,
      overpayment: overpayment,
      color: 'info'
    });
  }

  // 90 days plan (legacy option): No subscription discount
  if (options.show90Days) {
    let plan90Cost, plan90Annual;
    // No subscription discount - use bundle-discounted prices as-is
    plan90Cost = bonusEligibleBundleTotal + noBonusBundleTotal; // Use bundle-discounted prices without subscription discount
    plan90Annual = plan90Cost * 12;
    const plan90Quarterly = plan90Cost * 3; // 90 days = 3 months
    
    // Overpayment relative to 360 days (same as 360 days savings)
    const referenceMonthly = baseMonthlyCostNoDiscounts; // Use bundle-discounted prices as reference
    const overpayment = referenceMonthly * 12 - plan360Annual;

    plans.push({
      id: '90-days', 
      monthlyPrice: plan90Cost,
      annualPrice: plan90Annual,
      quarterlyPrice: plan90Quarterly,
      paymentAmount: plan90Quarterly, // 90 days payment
      isDefault: false,
      overpayment: overpayment,
      color: 'warning'
    });
  }

  // 12-month contract: No subscription discount (if monthly > 250)
  const referenceForThreshold = baseMonthlyCostNoDiscounts; // Use bundle-discounted total for threshold check
  if (options.show12Month && referenceForThreshold >= 250) {
    let plan12MonthCost, plan12MonthAnnual;
    // No subscription discount - use bundle-discounted prices as-is
    plan12MonthCost = bonusEligibleBundleTotal + noBonusBundleTotal; // Use bundle-discounted prices without subscription discount
    plan12MonthAnnual = plan12MonthCost * 12;
    
    // Overpayment relative to 360 days (same as 360 days savings)
    const referenceMonthly = baseMonthlyCostNoDiscounts; // Use bundle-discounted prices as reference
    const overpayment = referenceMonthly * 12 - plan360Annual;

    plans.push({
      id: '12-month-contract',
      monthlyPrice: plan12MonthCost,
      annualPrice: plan12MonthAnnual,
      paymentAmount: plan12MonthAnnual, // 12 months payment
      isDefault: false,
      overpayment: overpayment,
      color: 'default'
    });
  }

  // Split payment for Ukraine: No subscription discount
  if (options.showSplitPayment && region === 'ua') {
    let splitPaymentCost, splitPaymentAnnual;
    // No subscription discount - use bundle-discounted prices as-is
    splitPaymentCost = bonusEligibleBundleTotal + noBonusBundleTotal; // Use bundle-discounted prices without subscription discount
    splitPaymentAnnual = splitPaymentCost * 12;
    
    // Overpayment relative to 360 days (same as 360 days savings)
    const referenceMonthly = baseMonthlyCostNoDiscounts; // Use bundle-discounted prices as reference
    const overpayment = referenceMonthly * 12 - plan360Annual;

    plans.push({
      id: '360-days-split',
      monthlyPrice: splitPaymentCost,
      annualPrice: splitPaymentAnnual,
      paymentAmount: splitPaymentAnnual, // 360 days split payment
      isDefault: false,
      overpayment: overpayment,
      color: 'default'
    });
  }

  return {
    plans,
    baseMonthlyCost: baseMonthlyCostNoDiscounts, // Monthly cost without discounts (base prices)
    baseMonthlyCostNoDiscounts, // Monthly cost without any discounts (for calculations)
    showSavings,
    defaultPlan: plans[0],
    customDiscount360, // Include custom discounts for proper display
    customDiscount180
  };
};

/**
 * Format price display for subscription plans
 * @param {number} price - Price amount
 * @param {string} currency - Currency symbol
 * @returns {string} Formatted price
 */
export const formatPrice = (price, currency = '$') => {
  return `${currency}${Math.ceil(price)}`;
};

/**
 * Format savings/overpayment display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency symbol
 * @param {boolean} isOverpayment - Whether this is overpayment (vs savings)
 * @returns {Object} Formatted display object
 */
export const formatSavingsDisplay = (amount, currency = '$', isOverpayment = false) => {
  return {
    amount: Math.ceil(Math.abs(amount)),
    formattedAmount: `${currency}${Math.ceil(Math.abs(amount))}`,
    isOverpayment,
    text: isOverpayment ? 'overpayment' : 'savings'
  };
};

/**
 * Check if subscription settings should be visible
 * @param {number} monthlyTotal - Monthly total cost (base cost without subscription bonuses)
 * @param {string} region - Current region
 * @returns {Object} Visibility flags for different options
 */
export const getSubscriptionVisibility = (monthlyTotal, region) => {
  return {
    show12Month: monthlyTotal >= 250, // Show 12-month contract if base monthly cost >= 250
    showSplitPayment: region === 'ua', // Show split payment only for Ukraine
    showLegacyOptions: true, // 90 and 180 days always available in settings
  };
};